import { Button, Container, Grid, Group, em } from "@mantine/core";
import {
  IconCloudDownload,
  IconDownload,
  IconHandClick,
  IconStarFilled,
  IconStatusChange,
} from "@tabler/icons-react";
import { formatTime, getVersionStatus, resetProductSelection } from "@utils/CommonUtil";
import { getProduct, triggerUpdate } from "@utils/api-communication/RestApiCli";
import { useEffect, useState } from "react";

import DeviceInfoTable from "./DeviceInfoTable";
import DeviceLogsViewer from "./DeviceLogsViewer";
import DeviceStatusBadge from "../new_test_ui/new_test_steps/common/DeviceStatusBadge";
import TitleWithInfoLink from "../new_test_ui/new_test_steps/common/TitleWithInfoLink";
import UpdateTimeline from "./UpdateTimeline";
import { connect } from "react-redux";
import { createStyles } from "@mantine/emotion";
import { getWSService } from "@utils/api-communication/WebSocketCli";
import { openDeviceSetupPage } from "@utils/deviceSetupNavigation";
import { setRememberedDeviceUuid } from "@utils/deviceStorage";
import { sendStatusRequested } from "@utils/api-communication/WsUtil";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { USER_GUIDE_DOC_URL } from "@constants/externalTutorialLinks";

const useStyles = createStyles((_theme) => ({
  part: {
    [`@media (min-width: ${em(1000)})`]: {
      maxWidth: "50%",
    },
  },
  productTable: {
    width: "100%",
    margin: "0 auto",
  },
}));

function VersionControl(props) {
  const [pid, setPid] = useState(null);
  const [product, setProduct] = useState(null);
  const [canShowUpdateButton, setCanShowUpdateButton] = useState(false);
  const [versionData, setVersionData] = useState(null);
  const [osUpdateRequired, setOsUpdateRequired] = useState(false);
  const [appUpdateRequired, setAppUpdateRequired] = useState(false);
  const [updateMode, setUpdateMode] = useState(false);
  const [activeStepNo, setActiveStepNo] = useState(0);
  const [version, setVersion] = useState(props.version);
  const [availableVersions, setAvailableVersions] = useState([]);
  const [lastActionDate] = useState(new Date());
  const [timelineTimes, setTimelineTimes] = useState([]);
  const { t } = useTranslation();
  const { classes } = useStyles();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pid = params.get("pid");
    resetProductSelection(pid, "", "");
    if (pid) {
      setRememberedDeviceUuid(pid);
    }
    setPid(pid);
  }, []);

  useEffect(() => {
    if (pid) {
      getProduct(pid, (response) => {
        const pro = response.data;
        setProduct(pro);
        setVersion(pro.version);
        resetProductSelection(
          pid,
          pro.version,
          pro.puiVersion,
          true,
          pro.scopeName,
          pro.volume,
          pro.alias,
          pro.battery,
        );
        getVersionStatus(pro.version, pid).then(
          ({ osUpdateReq, appUpdateReq, versionData, availableVersions }) => {
            setOsUpdateRequired(osUpdateReq);
            setAppUpdateRequired(appUpdateReq);
            setVersionData(versionData);
            setAvailableVersions(availableVersions);
          },
        );
      });
    }
  }, [pid]);

  useEffect(() => {
    setVersion(props.version);
  }, [props.version]);

  useEffect(() => {
    if (!pid) return;
    getWSService();
    const timer = setTimeout(() => sendStatusRequested(pid), 1500);
    return () => clearTimeout(timer);
  }, [pid]);

  function submitUpdate(callback) {
    const url = "";
    const type = "app";
    let version;
    if (appUpdateRequired) {
      version = versionData?.latestApkVersion;
    }
    triggerUpdate(
      { pid, downloadUrl: url, type, version },
      (response) => {
        console.log("trigger update response", response);
        callback();
      },
      (error) => {
        console.log("trigger update error", error);
        alert("Error while triggering update");
      },
    );
  }

  useEffect(() => {
    const isUpdateRequired = appUpdateRequired;
    const isDownloadTriggered = activeStepNo > 0;
    setCanShowUpdateButton(isUpdateRequired && !isDownloadTriggered);
  }, [appUpdateRequired, activeStepNo, versionData, availableVersions]);

  const getTimeLineItems = () => {
    const timelineItems = new Array<{ icon; title; text; time }>();

    timelineItems.push(
      ...[
        {
          icon: <IconHandClick size={12} />,
          title: t("VersionControl.clickedUpdate"),
          text: t("VersionControl.clickedUpdateText"),
          time: "",
        },
        {
          icon: <IconCloudDownload size={12} />,
          title: t("VersionControl.downloadTriggered"),
          text: t("VersionControl.downloadTriggeredText"),
          time: "",
        },
        {
          icon: <IconDownload size={12} />,
          title: t("VersionControl.downloadSuccess"),
          text: t("VersionControl.downloadSuccessText"),
          time: "",
        },
        {
          icon: <IconStatusChange size={12} />,
          title: t("VersionControl.updateTriggered"),
          text: t("VersionControl.updateTriggeredText"),
          time: "",
        },
        {
          icon: <IconStarFilled size={12} />,
          title: t("VersionControl.finishWait"),
          text: t("VersionControl.finishWaitText"),
          time: "",
        },
      ],
    );

    timelineItems.forEach((item, index) => {
      const time = timelineTimes[index];
      item.time = time ? formatTime(time) : "";
    });
    return timelineItems;
  };

  return (
    product && (
      <Container size={"lg"} px="md" mt={"md"}>
        <TitleWithInfoLink
          title={t("UpdatePage")}
          tooltipText={t("InfoButtonTooltip")}
          link={USER_GUIDE_DOC_URL}
        />
        <Grid mt={"sm"}>
          <Grid.Col span={{ md: 6, sm: 12 }}>
            <DeviceInfoTable
              product={product}
              permissions={props.permissions ?? product?.states?.permissions}
              version={version}
              versionData={versionData}
              availableVersions={availableVersions}
              osUpdateRequired={osUpdateRequired}
              appUpdateRequired={appUpdateRequired}
              canShowUpdateButton={canShowUpdateButton}
              deviceStatusComponent={<DeviceStatusBadge dontPoll />}
              deviceStatus={props.deviceStatus}
              classes={classes}
              hideUsbUpdateModal={false}
              submitUpdate={(cb) =>
                submitUpdate(() => {
                  setUpdateMode(true);
                  setActiveStepNo(0);
                  setTimelineTimes((oldArray) => [...oldArray, new Date()]);
                  if (typeof cb === "function") cb();
                })
              }
            />
            {updateMode ? (
              <UpdateTimeline activeStepNo={activeStepNo} timelineItems={getTimeLineItems()} />
            ) : null}
            <Group mt="md">
              <Button variant="subtle" onClick={() => openDeviceSetupPage(navigate)}>
                {t("VersionControl.recoveryPrompt")}
              </Button>
            </Group>
          </Grid.Col>
          <Grid.Col span={{ md: 6, sm: 12 }}>
            <DeviceLogsViewer
              pid={pid}
              setActiveStepNo={setActiveStepNo}
              timeOffset={lastActionDate}
              setUpdateTimelineTimes={setTimelineTimes}
            />
          </Grid.Col>
        </Grid>
      </Container>
    )
  );
}

const mapStateToProps = (state) => {
  console.log("state", state.product.deviceStatus);
  return {
    deviceStatus: state.product.deviceStatus,
    version: state.product.currentVersion,
    permissions: state.product.permissions,
  };
};

export default connect(mapStateToProps)(VersionControl);
